import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo from "@/components/Seo";
import Editable from "@/components/Editable";
import { SERVICE_BY_SLUG } from "@/data/services";
import { ArrowRight, Check, MapPin, Phone, EnvelopeSimple, GraduationCap, House, PersonSimpleWalk, Coffee, HeartStraight, PawPrint, CaretLeft, X, Lock, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";

const ICONS = { GraduationCap, House, PersonSimpleWalk, Coffee, HeartStraight, PawPrint };

export default function ServiceDetail() {
  const { slug } = useParams();
  const staticSvc = SERVICE_BY_SLUG[slug];
  const [override, setOverride] = useState(null);
  const [showRequest, setShowRequest] = useState(false);
  const [showPay, setShowPay] = useState(false);

  // Try to fetch any admin override for this slug
  useEffect(() => {
    api.get("/services").then((r) => {
      const o = (r.data || []).find((s) => s.slug === slug);
      setOverride(o || null);
    }).catch(() => {});
  }, [slug]);

  // Track interest view — logged-in clients hitting this page get logged
  // so admins see "client viewed X" in the activity feed.
  useEffect(() => {
    if (!slug) return;
    api.post("/activity/track-interest", { kind: "service", ref_id: slug, label: slug }).catch(() => {});
  }, [slug]);

  // After Stripe returns the user to /services/:slug?paid_session=cs_xxx
  // verify payment status (public-friendly endpoint), then show a thank-you toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get("paid_session");
    if (!paid) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/checkout/private-status/${paid}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          toast.success("Payment received — the team will confirm your time within 24 hours.");
          // Clean the URL
          window.history.replaceState({}, "", window.location.pathname);
        } else if (r.data.payment_status === "initiated") {
          setTimeout(poll, 2000);
        }
      } catch (e) {
        // ignore — toast not critical
      }
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  if (!staticSvc && !override) return <Navigate to="/services" replace />;
  // Compose effective service — override fields take precedence over static
  const svc = staticSvc || {};
  const o = override || {};
  const Icon = ICONS[svc.icon] || GraduationCap;
  const title = o.title || svc.title;
  const price = o.price || svc.price;
  const duration = o.duration || svc.duration;
  const headline = o.headline || svc.headline;
  const desc = o.desc || svc.desc;
  const image = o.image || svc.image;
  const color = svc.color || "#f38838";
  // Resolve payment_mode: explicit override > legacy bookable_publicly bool > default request_only
  const paymentMode = o.payment_mode || (o.bookable_publicly ? "both" : "request_only");
  // Slugs whose services are book/pay-only — never show the "Request Time" form.
  const BOOK_ONLY_SLUGS = new Set(["puppy-school", "scent-detection", "activities-class", "teenage-puppy"]);
  const hideRequestTime = BOOK_ONLY_SLUGS.has(slug);
  const allowsPay = paymentMode === "pay_only" || paymentMode === "both" || hideRequestTime;
  const allowsRequest = !hideRequestTime && (paymentMode === "request_only" || paymentMode === "both");
  const priceAud = parseFloat(o.price_aud || 0);
  const bullets = (o.bullets && o.bullets.length ? o.bullets : svc.bullets) || [];
  const eligibility = o.eligibility || svc.eligibility;
  const faq = (o.faq && o.faq.length ? o.faq : svc.faq) || [];
  const availableLocations = o.available_locations || [];
  const intakeFormSlug = o.intake_form_slug || "";
  const related = (svc.related || []).map((s) => SERVICE_BY_SLUG[s]).filter(Boolean);
  const primary = (o.primary_cta_label || o.primary_cta_href)
    ? { label: o.primary_cta_label || "Get in touch", href: o.primary_cta_href || "/contact" }
    : (svc.cta_primary || { label: "Get in touch", href: "/contact" });
  const secondary = svc.cta_secondary || { label: "See locations", href: "/service-areas" };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid={`service-page-${slug}`}>
      <Seo
        title={`${title} | Puppl Sunshine Coast`}
        description={(desc || "").slice(0, 160)}
        canonical={typeof window !== "undefined" ? window.location.origin + `/services/${slug}` : ""}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": title,
            "description": desc || headline || "",
            "serviceType": svc.eyebrow || "Dog training service",
            "areaServed": { "@type": "Place", "name": "Sunshine Coast, Queensland" },
            "provider": { "@id": "https://puppl.app/#org" },
            "url": typeof window !== "undefined" ? window.location.origin + `/services/${slug}` : "",
            ...(priceAud > 0 && {
              "offers": {
                "@type": "Offer",
                "price": String(priceAud),
                "priceCurrency": "AUD",
                "availability": "https://schema.org/InStock",
                "url": typeof window !== "undefined" ? window.location.origin + `/services/${slug}` : "",
              },
            }),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://puppl.app/" },
              { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://puppl.app/services" },
              { "@type": "ListItem", "position": 3, "name": title, "item": typeof window !== "undefined" ? window.location.origin + `/services/${slug}` : "" },
            ],
          },
          ...(faq && faq.length
            ? [{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faq.map((f) => ({
                  "@type": "Question",
                  "name": f.q || f.question,
                  "acceptedAnswer": { "@type": "Answer", "text": f.a || f.answer },
                })),
              }]
            : []),
        ]}
      />
      <PublicHeader />

      {/* BREADCRUMB */}
      <div className="max-w-6xl mx-auto px-6 lg:px-12 pt-6">
        <Link to="/services" className="text-sm text-stone-500 hover:text-[#f38838] inline-flex items-center gap-1" data-testid="back-to-services">
          <CaretLeft size={14} /> All services
        </Link>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10 lg:py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 mb-4 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider" style={{ background: `${color}15`, color }}>
              <Icon size={14} weight="duotone" /> {svc.eyebrow || "Service"}
            </div>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-tight text-stone-900 mb-3" data-testid="service-title">{title}</h1>
            <Editable id={`service.${slug}.headline`} as="p" className="text-xl text-stone-700 mb-4" defaultHtml={headline} />
            <Editable id={`service.${slug}.desc`} as="p" className="text-stone-600 leading-relaxed mb-6" defaultHtml={desc} />
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="font-heading text-2xl font-bold" style={{ color }}>{price}</span>
              {duration && <><span className="text-stone-300">•</span><span className="text-stone-600">{duration}</span></>}
            </div>
            <div className="flex flex-wrap gap-3" data-testid="service-cta-row">
              {hideRequestTime ? (
                <Link to={primary.href || "/courses/in-person"} className="btn-primary inline-flex items-center gap-2" data-testid="service-cta-book-now">
                  <CreditCard size={16} weight="fill" /> {primary.label || "See All Intakes"} <ArrowRight size={14} weight="bold" />
                </Link>
              ) : allowsPay && priceAud > 0 ? (
                <button onClick={() => setShowPay(true)} className="btn-primary inline-flex items-center gap-2" data-testid="service-cta-book-pay">
                  <CreditCard size={16} weight="fill" /> Book & Pay ${priceAud.toFixed(0)} AUD <ArrowRight size={14} weight="bold" />
                </button>
              ) : allowsPay ? (
                <Link to={primary.href || (slug.includes("puppy-school") || slug.includes("activities") ? "/courses/in-person" : "/contact")} className="btn-primary inline-flex items-center gap-2" data-testid="service-cta-book-now">
                  <CreditCard size={16} weight="fill" /> {primary.label || "Book Now"} <ArrowRight size={14} weight="bold" />
                </Link>
              ) : null}
              {allowsRequest && (
                <button onClick={() => setShowRequest(true)} className={(hideRequestTime || allowsPay) ? "btn-secondary inline-flex items-center gap-2" : "btn-primary inline-flex items-center gap-2"} data-testid="service-cta-request-time">
                  <Lock size={16} weight="fill" /> Request Time <ArrowRight size={14} weight="bold" />
                </button>
              )}
              <Link to={secondary.href || "/service-areas"} className="btn-secondary inline-flex items-center gap-2" data-testid="service-cta-secondary">
                {secondary.label}
              </Link>
              {intakeFormSlug && (
                <Link to={`/intake/${intakeFormSlug}`} className="text-sm font-semibold text-[#1b4a76] hover:text-[#f38838] inline-flex items-center gap-1 px-3 py-2" data-testid="service-cta-intake-form">
                  Open intake form <ArrowRight size={12} weight="bold" />
                </Link>
              )}
            </div>
            {availableLocations.length > 0 && (
              <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="service-locations-row">
                <span className="text-xs uppercase tracking-wider font-semibold text-stone-500 inline-flex items-center gap-1"><MapPin size={12} weight="duotone" className="text-[#f38838]" /> Available at:</span>
                {availableLocations.map((locSlug) => (
                  <Link
                    key={locSlug}
                    to={`/service-areas/${locSlug}`}
                    className="text-xs font-semibold capitalize px-2.5 py-1 rounded-full bg-[#1b4a76]/10 text-[#1b4a76] hover:bg-[#1b4a76] hover:text-white transition-colors"
                    data-testid={`service-loc-badge-${locSlug}`}
                  >
                    {locSlug}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl">
            <img src={image} alt={title} className="w-full h-full object-cover" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Request Time modal */}
      {showRequest && <RequestTimeModal serviceSlug={slug} serviceTitle={title} onClose={() => setShowRequest(false)} />}

      {/* Book & Pay modal (Stripe) */}
      {showPay && <PayBookModal serviceSlug={slug} serviceTitle={title} priceAud={priceAud} onClose={() => setShowPay(false)} />}

      {/* WHAT'S INCLUDED */}
      <section className="py-12 px-6 lg:px-12 bg-white border-y border-stone-100">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10">
          <div>
            <p className="text-sm uppercase tracking-wider font-semibold text-stone-500 mb-2">What's included</p>
            <h2 className="font-heading text-3xl font-bold text-stone-900 mb-5">Everything you get with {svc.short_title || title}.</h2>
            <ul className="space-y-3" data-testid="service-bullets">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-stone-700">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${color}15` }}>
                    <Check size={14} weight="bold" style={{ color }} />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-5">
            {eligibility && (
              <div className="card-pp p-5">
                <p className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-1">Eligibility</p>
                <p className="text-sm text-stone-700">{eligibility}</p>
              </div>
            )}
            {svc.locations && (
              <div className="card-pp p-5">
                <p className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-2 flex items-center gap-2"><MapPin size={14} weight="duotone" className="text-[#f38838]" /> Locations</p>
                <ul className="text-sm text-stone-700 space-y-1">
                  {svc.locations.map((l, i) => <li key={i}>• {l}</li>)}
                </ul>
              </div>
            )}
            <div className="p-5 bg-[#1b4a76] text-white rounded-2xl shadow-sm" data-testid="service-contact-box">
              <p className="text-xs uppercase tracking-wider font-semibold text-[#E2B054] mb-2">Get in touch</p>
              <a href="tel:0435740077" className="flex items-center gap-2 mb-2 hover:text-[#E2B054] text-sm"><Phone size={14} weight="duotone" /> 0435 740 077</a>
              <a href="mailto:support@puppl.app" className="flex items-center gap-2 hover:text-[#E2B054] text-sm break-all"><EnvelopeSimple size={14} weight="duotone" /> support@puppl.app</a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="py-14 px-6 lg:px-12 max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-stone-900 mb-6">Common questions</h2>
          <div className="space-y-3">
            {faq.map((f, i) => (
              <details key={i} className="card-pp p-5 group" data-testid={`service-faq-${i}`}>
                <summary className="cursor-pointer font-heading font-semibold text-stone-900 list-none flex justify-between items-center">
                  {f.q}
                  <span className="text-[#f38838] group-open:rotate-180 transition-transform">▾</span>
                </summary>
                <p className="text-stone-600 mt-3 text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* RELATED SERVICES */}
      {related.length > 0 && (
        <section className="py-14 px-6 lg:px-12 bg-stone-100">
          <div className="max-w-6xl mx-auto">
            <p className="text-sm uppercase tracking-wider font-semibold text-stone-500 mb-2">You might also like</p>
            <h2 className="font-heading text-3xl font-bold text-stone-900 mb-6">Related services</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((r) => {
                const RIcon = ICONS[r.icon] || GraduationCap;
                return (
                  <Link key={r.slug} to={`/services/${r.slug}`} className="card-pp p-5 hover:-translate-y-0.5 transition-transform" data-testid={`related-${r.slug}`}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${r.color}15` }}>
                      <RIcon size={22} weight="duotone" style={{ color: r.color }} />
                    </div>
                    <div className="font-heading font-semibold text-stone-900 mb-1">{r.title}</div>
                    <div className="text-xs text-stone-500">{r.price} • {r.duration}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="py-14 px-6 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-stone-900 mb-3">Not sure which fits?</h2>
          <p className="text-stone-600 mb-6">Chat with our virtual receptionist in the bottom-right — it'll point you to the right service in under a minute.</p>
          <Link to="/services" className="btn-secondary inline-flex items-center gap-2">
            Compare all services <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <PublicFooter />
      <ChatWidget />
    </div>
  );
}

function RequestTimeModal({ serviceSlug, serviceTitle, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", dog_name: "", preferred_times: "", message: "" });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      // Create a Leads kanban entry as the request
      await api.post("/leads", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        dog_name: form.dog_name,
        interests: serviceSlug,
        message: `[Request Time — ${serviceTitle}] Preferred times: ${form.preferred_times}\n\n${form.message}`,
        source: "request-time",
      });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send request");
    }
  };
  return (
    <div className="fixed inset-0 bg-stone-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="request-time-modal">
        <div className="p-6 border-b border-stone-200 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-[#f38838]">Request Time</div>
            <h2 className="font-heading font-bold text-xl">{serviceTitle}</h2>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900"><X size={20} /></button>
        </div>
        {sent ? (
          <div className="p-8 text-center">
            <h3 className="font-heading text-2xl font-bold text-stone-900 mb-2">Request received!</h3>
            <p className="text-stone-600 mb-6">the team will personally review and get back to you within 24 hours to confirm or suggest alternative times.</p>
            <button onClick={onClose} className="btn-primary">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name *" className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm" data-testid="rt-name" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm" data-testid="rt-email" />
              <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
              <input value={form.dog_name} onChange={(e) => setForm({ ...form, dog_name: e.target.value })} placeholder="Dog's name" className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
            </div>
            <textarea required rows={2} value={form.preferred_times} onChange={(e) => setForm({ ...form, preferred_times: e.target.value })} placeholder="Your preferred days / times *" className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" data-testid="rt-times" />
            <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Anything else the team should know?" className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
            <p className="text-xs text-stone-500">the team reviews every request personally. You'll get a reply within 24 hours.</p>
            <button className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="rt-submit">Send request</button>
          </form>
        )}
      </div>
    </div>
  );
}

// Expose slugs at module level so consumers can iterate if needed.

function PayBookModal({ serviceSlug, serviceTitle, priceAud, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", dog_name: "", preferred_times: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);

  const finalAud = appliedCoupon ? appliedCoupon.final_aud : priceAud;

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    setCouponBusy(true);
    try {
      const r = await api.post("/coupons/validate", {
        code: coupon.trim(),
        amount_aud: priceAud,
        tx_type: "private_booking",
      });
      setAppliedCoupon(r.data);
      toast.success(`Coupon applied — ${r.data.discount_type === "percent" ? `${r.data.amount}% off` : `$${r.data.amount} off`}`);
    } catch (e) {
      setAppliedCoupon(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    } finally { setCouponBusy(false); }
  };

  const removeCoupon = () => { setAppliedCoupon(null); setCoupon(""); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout/private-booking", {
        service_slug: serviceSlug,
        ...form,
        coupon_code: appliedCoupon ? appliedCoupon.code : null,
        origin_url: window.location.origin,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Couldn't start checkout — please try again.");
        setBusy(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = err?.response?.status;
      toast.error(detail ? `${detail}${code ? ` (HTTP ${code})` : ""}` : `Couldn't start checkout${code ? ` (HTTP ${code})` : ""}`);
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-stone-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="pay-book-modal">
        <div className="p-6 border-b border-stone-200 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-[#f38838]">Book & Pay</div>
            <h2 className="font-heading font-bold text-xl">{serviceTitle}</h2>
            <p className="text-sm text-stone-500 mt-1">
              {appliedCoupon ? (
                <>
                  <span className="line-through text-stone-400">${priceAud.toFixed(2)}</span>{" "}
                  <span className="text-emerald-700 font-semibold">${finalAud.toFixed(2)} AUD</span> via Stripe
                </>
              ) : (
                <>${priceAud.toFixed(2)} AUD via Stripe</>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3" data-testid="pay-book-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name *" className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm" data-testid="pb-name" />
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm" data-testid="pb-email" />
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
            <input value={form.dog_name} onChange={(e) => setForm({ ...form, dog_name: e.target.value })} placeholder="Dog's name" className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
          </div>
          <textarea required rows={2} value={form.preferred_times} onChange={(e) => setForm({ ...form, preferred_times: e.target.value })} placeholder="Your preferred days / times *" className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" data-testid="pb-times" />
          <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Anything else the team should know?" className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />

          {/* Coupon row */}
          <div className="border-t border-stone-100 pt-3" data-testid="coupon-section">
            {appliedCoupon ? (
              <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm">
                <span className="text-emerald-800"><strong>{appliedCoupon.code}</strong> applied — saving ${appliedCoupon.discount_aud.toFixed(2)}</span>
                <button type="button" onClick={removeCoupon} className="text-xs text-emerald-700 hover:underline" data-testid="coupon-remove">Remove</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                  placeholder="Coupon code (optional)"
                  className="flex-1 rounded-lg border border-stone-200 px-3 py-2 text-sm font-mono uppercase"
                  data-testid="coupon-input"
                />
                <button
                  type="button"
                  onClick={applyCoupon}
                  disabled={couponBusy || !coupon.trim()}
                  className="px-3 py-2 rounded-lg border border-stone-300 text-sm font-semibold disabled:opacity-50 hover:bg-stone-50"
                  data-testid="coupon-apply"
                >
                  {couponBusy ? "..." : "Apply"}
                </button>
              </div>
            )}
          </div>

          <p className="text-xs text-stone-500">You'll be redirected to Stripe to complete payment. the team will then confirm an exact time from your preferred slots within 24 hours.</p>
          <button disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="pb-submit">
            <CreditCard size={16} weight="fill" /> {busy ? "Starting Stripe…" : `Pay $${finalAud.toFixed(0)} AUD with Stripe`}
          </button>
        </form>
      </div>
    </div>
  );
}
